import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { CsIconModule } from '@components/icon';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RegimenExpiryService } from '../../../../services/regimen-expiry-service/regimen-expiry.service';
import { ApiClientConstant } from '@cureskin/api-client';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { ConnectionService } from '@services/connection-service';
import { ProductCardComponent } from 'client/app/shop/components/product-card/product-card.component';

@Component({
  selector: 'regimen-expiry-bottomsheet',
  standalone: true,
  imports: [CommonModule, ProductCardComponent, CsIconModule, FormsModule],
  templateUrl: './regimen-expiry-bottomsheet.component.html',
})
export class RegimenExpiryBottomsheetComponent implements OnInit {
  isCouponApplied = false;
  discountPercentage = 0;
  totalMRP = 0;
  totalSP = 0;
  selectedProducts: { productId: string, quantity: number }[] = [];
  isCtaEnabled = true;
  showConfetti = false;
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: {
      allocatedDoctorImage: string;
      products: any[];
      showCoupon: boolean;
      couponDetails: {
        amount: number;
        couponCode: string;
      };
    },
    private readonly bottomSheetRef: MatBottomSheetRef<RegimenExpiryBottomsheetComponent>,
    private readonly router: Router,
    private readonly regimenExpiryService: RegimenExpiryService,
    private readonly eventLogger: EventLoggerService,
    private readonly experimentService: ExperimentService,
    private readonly conn: ConnectionService,
  ) {}

  ngOnInit(): void {
    this.initializeProductQuantities();
    this.applyCoupon();
    this.calculatePricing();
  }

  closeBottomSheet(): void {
    this.bottomSheetRef.dismiss();
  }

  applyCoupon(): void {
    const couponCodeDetails = this.regimenExpiryService.getCouponCodeDetails();
    if (couponCodeDetails.isCouponApplied && couponCodeDetails.couponCode) {
      this.isCouponApplied = true;
      this.data.couponDetails.couponCode = couponCodeDetails.couponCode;
    }
  }

  toggleCoupon(): void {
    if (!this.isCtaEnabled) {
      return;
    }
    this.isCouponApplied = !this.isCouponApplied;
    if (this.isCouponApplied) {
      this.playConfettiAnimation();
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'couponApplied_regimen_will_expire' }));
      this.regimenExpiryService.setCouponCodeDetails({
        couponCode: this.data.couponDetails.couponCode,
        isCouponApplied: true,
      });
    } else {
      this.regimenExpiryService.setCouponCodeDetails({
        couponCode: null,
        isCouponApplied: false,
      });
    }
    this.calculatePricing();
  }

  /**
   * Play confetti animation when coupon is applied
   */
  private playConfettiAnimation(): void {
    this.showConfetti = true;
    setTimeout(() => {
      this.showConfetti = false;
    }, 1500);
  }

  calculatePricing(): void {
    const productsWithQuantity = this.selectedProducts.filter((item: {quantity: number}) => item?.quantity > 0);
    this.isCtaEnabled = productsWithQuantity.length > 0;
    if (!this.isCtaEnabled) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'buyNow_regimen_will_expire_disabled' }));
    }

    if (productsWithQuantity.length === 0) {
      this.totalMRP = 0;
      this.totalSP = 0;
      this.discountPercentage = 0;
      return;
    }

    this.calculateProductTotals(productsWithQuantity);
    this.applyCouponDiscount();
  }

  private calculateProductTotals(productsWithQuantity: Array<{productId: string, quantity: number}>): void {
    const productQuantityMap = new Map<string, number>();
    productsWithQuantity.forEach(item => {
      productQuantityMap.set(item.productId, item.quantity);
    });

    // Filtering products that are actually selected
    const selectedProducts = this.data.products.filter((product: any) =>
      productQuantityMap.has(product.objectId)
    );

    this.totalMRP = selectedProducts.reduce((acc: number, product: any) => {
      const quantity = productQuantityMap.get(product.objectId) || 0;
      return acc + (product.MRP * quantity);
    }, 0);

    this.totalSP = selectedProducts.reduce((acc: number, product: any) => {
      const quantity = productQuantityMap.get(product.objectId) || 0;
      return acc + (product.SP * quantity);
    }, 0);
  }

  private applyCouponDiscount(): void {
    // Apply coupon discount if applicable
    if (this.isCouponApplied && this.data?.couponDetails?.amount) {
      this.totalSP = Math.max(0, this.totalSP - this.data.couponDetails.amount);
    }

    this.totalMRP = Math.round(this.totalMRP);
    this.totalSP = Math.round(this.totalSP);

    const totalDiscount = this.totalMRP - this.totalSP;
    this.discountPercentage = Math.floor((totalDiscount / this.totalMRP) * 100);
  }

  initializeProductQuantities(): void {
    const existingProductData = this.regimenExpiryService.getSelectedProducts();
    const hasNonZeroQuantity = existingProductData.some((item: {productId: string, quantity: number}) => item?.quantity > 0);
    if (existingProductData.length > 0 && hasNonZeroQuantity) {
      this.selectedProducts = [...existingProductData];
    } else {
      this.generateProductQuantities();
      this.regimenExpiryService.setSelectedProducts(this.selectedProducts);
    }
  }

  generateProductQuantities(): void {
    const regimenClass = this.regimenExpiryService.getRegimenClass(this.data.products);
    this.data.products.forEach((product: any) => {
      this.selectedProducts.push({
        productId: product.objectId,
        quantity: this.shouldAddProduct(product, regimenClass) ? 1 : 0,
      });
    });
  }

  shouldAddProduct(product: any, regimenClass: string): boolean {
    if (product.isPurchasedInLast30Days) {
      return false;
    }
    if (regimenClass === 'BOTH' && (product?.category === ApiClientConstant.Regimen.Class.HAIR || product?.category === ApiClientConstant.Regimen.Class.BODY)) {
      return false;
    }
    return true;
  }

  addProduct({ product, image }: { product: any, image: HTMLImageElement }): void {
    if (!product) return;
    const productIndex = this.selectedProducts.findIndex((item: {productId: string, quantity: number}) => item.productId === product.objectId);
    if (productIndex !== -1) {
      this.selectedProducts[productIndex].quantity += 1;
      // Update service with new quantity
      this.regimenExpiryService.updateProductQuantity(product.objectId, this.selectedProducts[productIndex].quantity);
      this.calculatePricing();
    }
  }

  removeProduct(product: any): void {
    if (!product) return;
    const productIndex = this.selectedProducts.findIndex((item: {productId: string, quantity: number}) => item.productId === product.objectId);
    if (productIndex !== -1) {
      this.selectedProducts[productIndex].quantity -= 1;
      // Update service with new quantity
      this.regimenExpiryService.updateProductQuantity(product.objectId, this.selectedProducts[productIndex].quantity);
      this.calculatePricing();
    }
  }

  buyNow(): void {
    if (!this.isCtaEnabled) {
      return;
    }

    const actualProducts = this.selectedProducts.filter((item: {quantity: number}) => item?.quantity > 0);

    // Generate product IDs array with repeated IDs based on quantity
    const productIds = actualProducts.flatMap((item: {productId: string, quantity: number}) =>
      Array(item.quantity).fill(item.productId)
    );

    const productIdsString = productIds.join(',');
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'buyNow_regimen_will_expire' }));
    this.bottomSheetRef.dismiss();
    const user = this.conn.getActingUser();
    const orderState = user?.get('orderState');
    this.router.navigate(['/checkout'], {
      queryParams: {
        type: 'PRODUCT',
        products: productIdsString,
        from: 'regimenExpiry',
        useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(orderState))
      }
    });
  }
}
